/**
 * SectionView — dispatcher por tipo de sección.
 *
 * Recibe la sección entera + brand + onChange tipado por sección.
 * Internamente despacha al componente concreto. Es el único lugar donde
 * se hace `switch (section.type)`; el resto del editor es agnóstico.
 */
import type { PlanBrand, PlanSection } from "../../../types/plan";
import { SectionIdContext } from "../EditorStyleContext";
import { SectionBrandContext, SectionDecorationsContext } from "./section-shared";
import { CardioSection } from "./CardioSection";
import { ClientSection } from "./ClientSection";
import { CoachBioSection } from "./CoachBioSection";
import { CoverSection } from "./CoverSection";
import { DietSection } from "./DietSection";
import { MeasurementsSection } from "./MeasurementsSection";
import { NotesSection } from "./NotesSection";
import { RoutineSection } from "./RoutineSection";
import { SupplementsSection } from "./SupplementsSection";

interface SectionViewProps {
  section: PlanSection;
  brand: PlanBrand | null;
  onChange: (next: PlanSection) => void;
  readonly?: boolean;
  /** Callback opcional para abrir el modal de generación con IA con un toggle
   *  pre-seleccionado. Solo lo pasa el editor — `PrintablePlanView` y la vista
   *  pública lo dejan undefined → el botón "Generar por IA" no se renderiza.
   *  Esto garantiza que el coach lo ve pero el cliente final del entrenador NO. */
  onGenerateWithAi?: (kind: "training" | "nutrition") => void;
  /** Cliente vinculado al plan (para "Importar medidas del cliente"). */
  clientId?: number | null;
}

export function SectionView(props: SectionViewProps) {
  // Propaga sectionId via context para que cada InlineEditable interno
  // pueda persistir styles sin tener que recibir sectionId por prop.
  // También propaga `brand` para que `SectionPage` pueda renderizar la
  // marca de agua del coach sin tener que recibirlo por prop en cascada.
  // Y `decorations` para los íconos flotantes tipo Canva.
  const decorationsValue = {
    decorations: props.section.decorations || [],
    onChange: props.readonly
      ? null
      : (next: PlanSection["decorations"]) =>
          props.onChange({ ...props.section, decorations: next || [] }),
  };
  return (
    <SectionBrandContext.Provider value={props.brand}>
      <SectionDecorationsContext.Provider value={decorationsValue}>
        <SectionIdContext.Provider value={props.section.id}>
          <SectionViewInner {...props} />
        </SectionIdContext.Provider>
      </SectionDecorationsContext.Provider>
    </SectionBrandContext.Provider>
  );
}

function SectionViewInner({ section, brand, onChange, readonly, onGenerateWithAi, clientId }: SectionViewProps) {
  switch (section.type) {
    // Nota: NO pasamos `sectionId` por prop a cada subsección — el SectionIdContext
    // del wrapper ya lo propaga automáticamente a todos los <InlineEditable> internos.
    case "cover":
      return (
        <CoverSection
          data={section.data}
          brand={brand}
          readonly={readonly}
          onChange={(patch) => onChange({ ...section, data: { ...section.data, ...patch } })}
        />
      );
    case "client":
      return (
        <ClientSection
          data={section.data}
          readonly={readonly}
          onChange={(patch) => onChange({ ...section, data: { ...section.data, ...patch } })}
        />
      );
    case "measurements":
      return (
        <MeasurementsSection
          data={section.data}
          readonly={readonly}
          clientId={clientId}
          onChange={(next) => onChange({ ...section, data: next })}
        />
      );
    case "coachBio":
      return (
        <CoachBioSection
          data={section.data}
          brand={brand}
          readonly={readonly}
          onChange={(patch) => onChange({ ...section, data: { ...section.data, ...patch } })}
        />
      );
    case "routine":
      return (
        <RoutineSection
          data={section.data}
          readonly={readonly}
          onChange={(next) => onChange({ ...section, data: next })}
          onGenerateWithAi={onGenerateWithAi ? () => onGenerateWithAi("training") : undefined}
        />
      );
    case "cardio":
      return (
        <CardioSection
          data={section.data}
          readonly={readonly}
          onChange={(next) => onChange({ ...section, data: next })}
        />
      );
    case "diet":
      return (
        <DietSection
          data={section.data}
          readonly={readonly}
          onChange={(next) => onChange({ ...section, data: next })}
          onGenerateWithAi={onGenerateWithAi ? () => onGenerateWithAi("nutrition") : undefined}
        />
      );
    case "supplements":
      return (
        <SupplementsSection
          data={section.data}
          readonly={readonly}
          onChange={(next) => onChange({ ...section, data: next })}
        />
      );
    case "notes":
      return (
        <NotesSection
          data={section.data}
          brand={brand}
          readonly={readonly}
          onChange={(next) => onChange({ ...section, data: next })}
        />
      );
    default:
      // El switch es exhaustivo — TS no debería llegar aquí.
      return null;
  }
}
